import React from 'react';
import PropTypes from 'prop-types';
import { Switch, Route, withRouter, Redirect } from 'react-router-dom';
import UsersCreate from './UsersCreate';
import UsersUpdate from './UsersUpdate';
import UsersUpdateClient from './UsersUpdateClient';
import UsersLogin from './UsersLogin';
import UsersUpdateCredentials from './UsersUpdateCredentials';
import UsersResetPassword from './UsersResetPassword';
import { USERS, USERS_STEPS, EVAL_INTRO } from './ConstPaths';
import UsersProviderView from './UsersProviderView';
import UsersReview from './UsersReview';
import UsersCreateClient from './UsersCreateClient';
import UsersRequestAccess from './UsersRequestAccess';
import { ROLES, LOGIN_STATUS, CREATE_STATUS } from './ConstUsers';

const propTypes = {
  provider: PropTypes.object,
  providerClients: PropTypes.array,
  client: PropTypes.object,
  resetToken: PropTypes.string,
  stayLoggedIn: PropTypes.bool,
  checkUserCredentials: PropTypes.func.isRequired,
  updateUserCredentials: PropTypes.func.isRequired,
  resetPassword: PropTypes.func.isRequired,
  updateSession: PropTypes.func.isRequired,
  createUser: PropTypes.func.isRequired,
  updateUser: PropTypes.func.isRequired,
  getAllClients: PropTypes.func.isRequired,
  createClientAccessRequest: PropTypes.func.isRequired
};

const defaultProps = {

};

//Manage the wizard form for the Get Started section
//Initialize the forms with props and manage those as local state.
//On Continue, update the global Session state with the local state from the forms. 
// Following pattern described in https://goshacmd.com/wizard-form-collect-info/
//NOTE: this 'wizard' has only 1 step, so we don't currently need to step management,
//but the local state, componentDidUpdate, etc. from the wizard pattern is all useful

const BASE_URL = USERS + "/";
//the steps of the wizard form:
const steps = USERS_STEPS;

const NEXT_SECTION = EVAL_INTRO;

class UsersTab extends React.Component {
  constructor(props) {
    super(props);
    console.log("*UsersTab: start constructor");

    // define the initial value for role, used to set state.role below
    // Potential gotcha if we rejigger the routes
    let role;
    switch(this.props.location.pathname){
      case BASE_URL + steps.CREATE:
      case BASE_URL + steps.LOGIN:
        role = ROLES.PROVIDER;
        break;
      case BASE_URL + steps.CREATE_CLIENT:
        role = ROLES.CLIENT;
        break;
      default:
        break;
    }

    this.state = {
      provider: this.props.provider,
      client: this.props.client,
      user:null,
      providerUsername: this.props.provider ? this.props.provider.username : '',
      clientUsername: this.props.client ? this.props.client.username : '',
      password: '',
      newPassword: '',
      providerAlias: this.props.provider ? this.props.provider.alias : '',
      clientAlias: this.props.client ? this.props.client.alias : '',
      providerPronouns: this.props.provider ? this.props.provider.pronouns :  ["they", "them", "their"],
      clientPronouns: this.props.client ? this.props.client.pronouns : ["they", "them", "their"],
      providerFirstPronoun: this.props.provider ? this.props.provider.pronouns[0] : 'they',
      clientFirstPronoun: this.props.client ? this.props.client.pronouns[0] : 'they',
      role: role,
      email: '',
      resetToken: this.props.resetToken,
      stayLoggedIn: this.props.stayLoggedIn,
      providerClients: null,
      selectedClient: null,
      requestClientUsername: '',
      requestClientClinicianEmail: ''
     };

    this.goToCreateUser = this.goToCreateUser.bind(this);
    this.goToCreateClient = this.goToCreateClient.bind(this);
    this.goToRequestAccess = this.goToRequestAccess.bind(this);
    this.goToUpdateUser = this.goToUpdateUser.bind(this);
    this.goToUpdateCredentials = this.goToUpdateCredentials.bind(this);
    this.onResetPassword = this.onResetPassword.bind(this);
    this.logout = this.logout.bind(this);
    this.saveClientToSession = this.saveClientToSession.bind(this);
    this.saveProviderToSession = this.saveProviderToSession.bind(this);
    this.navToNextSection = this.navToNextSection.bind(this);
    this.onContinue = this.onContinue.bind(this);

    console.log("*UsersTab: end constructor");

  }

  //put side effects here, rather than in the constructor
  componentDidMount(){
    console.log(" UsersTab start componentDidMount");
    this.updateClientsAndSession(this.props.provider, this.props.client);
    this.validateEmail(this.state.email);
    console.log(" UsersTab end componentDidMount");
  }

  //If new props come in after rendering, update the views in this tab.
  //(This occurs when you refresh a view and it pulls data from the database.)
  componentDidUpdate(prevProps, prevState){
    //for simple strings
    if (this.props.provider !== prevProps.provider){
      console.log("--incoming props: " + this.props.provider + " differ from prev props: " + prevProps.provider);
      this.setState({'provider': this.props.provider}, () => this.updateClientsAndSession(this.props.provider, this.props.client));
    }
    if (this.props.client !== prevProps.client){
      console.log("--incoming props: " + this.props.client + " differ from prev props: " + prevProps.client);
      this.setState({'client': this.props.client}, () => this.updateClientsAndSession(this.props.provider, this.props.client));
    }
    if(this.props.stayLoggedIn !== prevProps.stayLoggedIn){
      console.log("--incoming props: " + this.props.stayLoggedIn + " differ from prev props: " + prevProps.stayLoggedIn);
      this.setState({'stayLoggedIn': this.props.stayLoggedIn});
    }
    if(this.props.providerClients !== prevProps.providerClients){
      console.log("--incoming props: " + this.props.providerClients + " differ from prev props: " + prevProps.providerClients);
      // If providerClients is updated, we need to update the selectedClient as well
      // client may be null, so we check for that using safe navigation operator
      this.setState({
        'providerClients': this.props.providerClients,
        'selectedClient': prevState.selectedClient ? this.props.selectedClient : this.props.client?.id
      });
    }
  }
  
    //shows when componentWillUnmount is called (trying to debug jest test)
    componentWillUnmount(){
      console.log(" UsersTab start componentWILLUnmount");
      console.log(" UsersTab end componentWILLUnmount");
    }
  
  //Update the local state with info from either the provider and/or client object.
  //Also pulls info from the database.
  //Called when UsersTab is first mounted (componentDidMount()),
  //and when provider or client props change, or when setState called (componentDidUpdate())
  async updateClientsAndSession(provider, client){
    console.log("*UsersTab: start updateClientsAndSession. provider: " + JSON.stringify(provider) + " client: " + JSON.stringify(client) + " this.state.providerUsername: " + this.state.providerUsername + " this.state.clientUsername: " + this.state.clientUsername);
    //If provider or client come in as null (e.g., after a logout), make sure we reset the state associated with a previous provider or client
    //providerClients gets built after you login -- it builds a list of the clients associated with you
    let clients = provider ? this.state.providerClients : null;
    // client may be null, so we check for that using safe navigation operator
    let selectedClient = provider ? this.state.selectedClient ? this.state.selectedClient : client?.id : null;

    //Why getting the provider's clients again? this may be here to make sure that we have the most updated values for the clients
    //Also if you come in to UsersTab with an existing provider.
    if(provider){
      clients = await this.props.getAllClients(provider);
    }
    let role = ROLES.OTHER;
    let providerUsername = provider ? provider.username : '';
    let clientUsername = client ? client.username : '';
    let providerAlias = provider ? provider.alias : '';
    let clientAlias = client ? client.alias : ''; 
    let providerPronouns = provider ? provider.pronouns : ["they", "them", "their"];
    let clientPronouns = client ? client.pronouns : ["they", "them", "their"];
    let providerFirstPronoun = provider ? provider.pronouns[0] : "they";
    let clientFirstPronoun = client ? client.pronouns[0] : "they";
    // Infer which object is the relevant one based on the current route you're on.
    // TODO Could be a gotcha if we decide to modify the routes
    // switch(this.props.location.pathname){
    //   case BASE_URL + steps.CREATE:
    //   case BASE_URL + steps.LOGIN:
    //   case BASE_URL + steps.PRACTITIONER_VIEW:
    //   case BASE_URL + steps.UPDATE_USER:
    //     role = ROLES.PROVIDER;
    //     if(provider){
    //       console.log("**** updateClientsAndSession about to set providerUsername to: " + provider.providerUsername);
    //       providerUsername = provider.username;
    //       providerAlias = provider.alias;
    //       providerPronouns = provider.pronouns;
    //       providerFirstPronoun = (Array.isArray(providerPronouns) && providerPronouns[0]) ? providerPronouns[0] : '';
    //     }
    //     break;
    //   case BASE_URL + steps.CREATE_CLIENT:
    //   case BASE_URL + steps.UPDATE_CLIENT:
    //     role = ROLES.CLIENT;
    //     if(client){
    //       console.log("there already is a client! alias: " + client.alias);
    //       clientUsername = client.username;
    //       clientAlias = client.alias;
    //       clientPronouns = client.pronouns;
    //       clientFirstPronoun = (Array.isArray(clientPronouns) && clientPronouns[0]) ? clientPronouns[0] : '';
    //     }
    //     break;
    //   default:
    //     break;
    // }
    //Note here that the password is set to '' (so will not reflect any password passed in via the provider prop)
    this.setState({
      provider: provider,
      client: client,
      user: null,
      providerUsername: providerUsername,
      clientUsername: clientUsername,
      password: '',
      newPassword: '',
      userStatus: -1,
      providerAlias: providerAlias,
      clientAlias: clientAlias,
      providerPronouns: providerPronouns,
      clientPronouns: clientPronouns,
      providerFirstPronoun: providerFirstPronoun,
      clientFirstPronoun: clientFirstPronoun,
      role: role,
      email: '',
      providerClients: clients,
      selectedClient: selectedClient,
    }, () => {
      console.log("*UsersTab: end updateClientsAndSession. this.state.providerUsername: " + this.state.providerUsername + "this.state.clientUsername: " + this.state.clientUsername +  " this.state.providerClients: " + this.state.providerClients +  " this.state.providerFirstPronoun: " + this.state.providerFirstPronoun + " this.state.clientFirstPronoun: " + this.state.clientFirstPronoun);
    });
  }

  //Save the local client object to the App.session
  saveClientToSession = () => {
    console.log("updating client: " + this.state.client);
    this.props.updateSession('client', this.state.client);
  }

  //Save the local provider object to the App.session, along with stayLoggedIn T/F value.
  //Called from onContinue when you move to a new page from /login
  // Previously included check of !window.runningTest. This was a holdover from when we forced users to navigate through
  // login to start using the app and has been removed.
  saveProviderToSession = () => {
    this.props.updateSession('provider', this.state.provider);
    this.props.updateSession('stayLoggedIn', this.state.stayLoggedIn);
  }

  navToNextSection = () => {
    console.log("    pushing new route: " + NEXT_SECTION);
    const curURL = new URL(window.location.href);

    this.props.history.push({
      pathname: NEXT_SECTION,
      search: curURL.search
    });
  };

  //Get the current step from the URL
  getCurrentStep(){
    //e.g. URL of '/get-started/names' would have step of 'names'
    const stepParam = this.props.match.params.step;
    if(stepParam){
      console.log("Users.getCurrentStep: got step from URL");
      return(stepParam);
    }
    else{
      console.log("Users.getCurrentStep: using default step");
      return(steps.LOGIN);
    }
  }

  //On Continue for a given step:
  // figure out the next step and go to it
  //For the UsersTab, this is more complicated than the usual pattern --
  //TODO it does not follow the pattern of having a useEffect() hook in each child handle the saving of form data.
  //The pattern used elsewhere simplifies onContinue because then all it has to do is figure out the next route.
  //This onContinue handles both the data and the routing -- I guess because we have to do things with the data like retrieve the associated user.
  //**Q** But the routing is confusing -- why navigating within the switch, as part of the setState callback?
  async onContinue() {
    let history = this.props.history;
    let nextStep = '';
    let nextRoute = '';
    let goToNextPage = false;
    let goToNextSection = false;
    let clients = [];
    const curURL = new URL(window.location.href);


    console.log("Start of UsersTab.onContinue");
    switch(this.getCurrentStep()){
      //on /login page, username and password are currently for the provider
      //it's possible in the future it might be used for the client as well, but not right now
      case steps.LOGIN:
        //if you've hit Continue on /login without entering either a username or password
        // TODO this essentially logs you out.  But now we have a logout method so maybe not necessary?
        // May want to keep this to allow you to move forward if those 2 fields are left empty
        //OK, let's have it move you forward in that case, but not log you out. It just won't affect provider at all.
        if(!this.state.providerUsername && !this.state.password){
          //TODO can we just set goToNextSection = true and then nav to next section outside of this switch?
          goToNextPage = true;
          goToNextSection = true;
          this.navToNextSection();
        }
        //if you've entered a username and/or password for provider
        else{
          //checkLogin retrieves the associated user from the database and ** sets the local state.user object ** based on return from App.checkUserCredentials().
          //this.state.user will then contain the role that is stored with that user
          goToNextPage = await this.checkLogin(this.state.providerUsername, this.state.password);
          //True if a matching user was found:
          if(goToNextPage){
            switch(this.state.user.role){
              //For providers, retrieve the associated clients, so we can show them in the practitioner-view
              case ROLES.PROVIDER:
                // clients = null;
                nextStep = steps.PRACTITIONER_VIEW;
                this.setState({
                  'provider': this.state.user,
                }, async() => {
                  this.saveProviderToSession();
                  clients = await this.props.getAllClients(this.state.user);
                  this.setState({
                    'providerClients': clients,
                    'selectedClient': ''
                  });
                });
                break;
              //Case of clients logging in is not yet supported
              case ROLES.CLIENT:
              default:
                break;
            }
          }
        }
        break;
      // Used to make a new provider account (role === 1).
      // (/create-client is a separate route to make a new client account)
      // This actually logs the new provider in as well, via saveProviderToSession()
      // An unambiguous condition that shows that someone is logged in: a truthy this.state.provider object.
      // And that gets saved to App.Session global state as well (saveProviderToSession) 
      // Note Erik's points that login process is completely finished when we have a client (truthy this.state.client). Session is tied to a client.
      // Provider can pick a client they are working with, and we get the session from that client.
      case steps.CREATE:
        if(this.state.providerUsername && this.state.password){
          // Put the user defined by the local state fields (state.username, state.password, etc.) into the database
          //If the user already exists in the database, it will patch it in. Otherwise will insert a new user
          //createNewUser also sets local state.user
          await this.createNewUser(this.state.providerUsername, this.state.password, this.state.providerAlias, this.state.providerPronouns, ROLES.PROVIDER, this.state.email);
          //if something went wrong when saving, stay on this page. (this.state.user === null in that case)
          goToNextPage = !!this.state.user;
          if(goToNextPage){
            clients = await this.props.getAllClients(this.state.user);
            nextStep = steps.PRACTITIONER_VIEW;
            this.setState({
              'provider': this.state.user,
              'providerClients': clients,
              'selectedClient': ''
            }, () => {
              this.saveProviderToSession();
            });
          }
        }
        else{
          //turns on the red border for the form fields
          this.setState({
            'userStatus': CREATE_STATUS.NOTHING
          });
          alert("You must enter a username and password to sign up for a practitioner account.")
        }
        break;
      case steps.PRACTITIONER_VIEW:
        goToNextPage = true;
        if(this.state.selectedClient){
          // Get the selected client from the provider's list
          var client = this.state.providerClients.filter(client => client.id === this.state.selectedClient)[0];
          //TODO do we need to navToNextSection like this, or can we do it outside the switch block?
          goToNextSection=true;
          this.setState({
            'client': client,
          }, () => {
            this.saveClientToSession();
            this.navToNextSection();
          });
        }else{
        // If they didn't select a client, we can proceed without one (but no data will be saved)
        this.setState({
          'client': null
        }, () => {
          this.saveClientToSession();
          this.navToNextSection();
        });

        }
        break;
      case steps.CREATE_CLIENT:
        if(this.state.clientUsername){
          // Put the user defined by the local state fields (state.username, state.password, etc.) into the database
          // We know the user doesn't already exist because we just checked above.
          //(Type of user determined by this.state.role, which in this case must be 2 to create a client)
          //createNewUser also sets local state.user
          await this.createNewUser(this.state.clientUsername, '', this.state.clientAlias, this.state.clientPronouns, ROLES.CLIENT, '');
          //if something went wrong when saving, stay on this page. (this.state.user === null in that case)
          goToNextPage = !!this.state.user;
          goToNextSection = goToNextPage;
          this.setState({
            client: this.state.user,
          }, () => {
            this.saveClientToSession();
            if (goToNextPage && goToNextSection) {
              this.navToNextSection();
            } 
          });
        }
        else{
          //turns on the red border for the form fields
          this.setState({
            'userStatus': CREATE_STATUS.NOTHING
          });
          alert("You must enter a username to create a client.")
        }
        break;
      case steps.UPDATE_USER:
        //If someone is logged in, update the provider with the values from the form
        if(this.state.provider){
          //alert("We are updating the practitioner with this info");
          await this.updateUser(this.state.providerUsername, this.state.providerAlias, this.state.providerPronouns, this.state.email);
          this.setState({
            'provider': this.state.user,
          }, () => {
            this.saveProviderToSession();
          });
        }
        else{
          alert("No one is logged in, so there's nothing to update.");
        }
        history.goBack();
        break;
      case steps.UPDATE_CLIENT:
        //If there is a current client, update that client with the values from the form
        if(this.state.client){
          //alert("We are updating the client with this info");
          await this.updateClient(this.state.clientUsername, this.state.clientAlias, this.state.clientPronouns);
          this.setState({
            'client': this.state.user,
          }, () => {
            this.saveClientToSession();
          });
        }
        else{
          alert("There is no active client, so there's nothing to update.");
        }
        history.goBack();
        break;
  
      // These steps are not on the main workflow:
      case steps.REVIEW:
        goToNextPage = true;
        goToNextSection = true;
        break;
      // Allows provider to change their password. Currently available by clicking Update Password on /practitioner-view.
      // Successful (userStatus === 2) if able to login with current password. If success, patches the database with newPassword.
      case steps.UPDATE_CREDENTIALS:
        var userStatus = await this.props.updateUserCredentials(this.state.providerUsername, this.state.password, this.state.newPassword, '', '');
        this.setState({
          userStatus: userStatus
        });
        switch(userStatus){
          case LOGIN_STATUS.SUCCESS:
              this.props.updateSession('tempMessage', {
                icon:'success',
                title:'Password Updated',
                label: '',
                message: 'Your password has been successfully updated. ',
                duration: 4000
              });
            break;
          case LOGIN_STATUS.NO_USER:
            alert("User doesn't exist");
            break;
          case LOGIN_STATUS.MISMATCH:
            alert("User exists, but password is incorrect");
            break;
          default:
            break;
        }
        //Continue to /practitioner-view, if successful, since that is where you started (sort of like a pop-up)
        nextStep = steps.PRACTITIONER_VIEW;
        goToNextPage = (userStatus === LOGIN_STATUS.SUCCESS);
        break;
      // This case only occurs when you click the emailed reset link 
      // (e.g.,  http://localhost:3000/users/reset-password?token=1da9dd07-9355-43be-9e0a-908d300d8da4)
      // works the same way as UPDATE_CREDENTIALS except with a reset token taken from the URL. 
      case steps.RESET_PASSWORD:
        var userResetStatus = await this.props.updateUserCredentials('', '', this.state.newPassword, '',this.state.resetToken);
        this.setState({
          userStatus: userResetStatus
        });
        switch(userResetStatus){
            case LOGIN_STATUS.SUCCESS:
              this.props.updateSession('tempMessage', {
                icon:'success',
                title:'Password Updated',
                label: '',
                message: 'Your password has been successfully updated. ',
                duration: 4000
              });
            break;
          // **Q** When would this happen?
          case LOGIN_STATUS.NO_USER:
            alert("Sorry, we couldn't find a matching user for this token. Please go to the Login page and try Forgot Password again.");
            break;
          case LOGIN_STATUS.MISMATCH:
            alert("Sorry, this token has expired. Please go to the Login page and try Forgot Password again.");
            break;
          default:
            break;
        }
        //Continue to /login if userResetStatus is success
        // Then user is supposed to login with their new password.
        nextStep = steps.LOGIN;
        goToNextPage = (userResetStatus === LOGIN_STATUS.SUCCESS);
        break;
      case steps.REQUEST_ACCESS:
        if(!this.state.requestClientUsername){
          alert("You must enter an existing client username to request access to a client.");
          goToNextPage = false;
        }
        else
        {
          await this.props.createClientAccessRequest(this.state.requestClientClinicianEmail, this.state.requestClientUsername);
          goToNextPage = true;
        }
        nextStep = steps.PRACTITIONER_VIEW;
        break;
      default:
        console.log("   default next step");
        nextStep = steps.LOGIN;
    }
    if (goToNextPage && !goToNextSection) {
      nextRoute = BASE_URL + nextStep;
      console.log("    pushing new route: " + nextRoute);
      history.push({
        pathname: nextRoute,
        search: curURL.search
      });
    } 
  }

  async getUniqueClientUsername(){
    return("uniqueClientUsername");
  }

  //Button handler that routes you to /create so you can create a new account (currently included on the /login page)
  async goToCreateUser(){
    this.setState({'role': ROLES.PROVIDER});
    let history = this.props.history;
    let nextRoute = BASE_URL + USERS_STEPS.CREATE;
    const curURL = new URL(window.location.href);

    console.log("    pushing new route: " + nextRoute);
    history.push({
      pathname: nextRoute,
      search: curURL.search
    });
  }

  //Button handler that routes you to /update-user so you can update provider info
  goToUpdateUser(){
    console.log("this.state.provider: " + this.state.provider);
    this.setState({
      'providerUsername': this.state.provider ? this.state.provider.username : '',
      'password': this.state.provider ? this.state.provider.password : '',
      'providerAlias': this.state.provider ? this.state.provider.alias : '',
      'providerPronouns': this.state.provider ? this.state.provider.pronouns : [],
      'providerFirstPronoun': (this.state.provider && Array.isArray(this.state.provider.pronouns)) ? this.state.provider.pronouns[0] : '',
      'email': this.state.provider ? this.state.provider.email : '',
      'role': ROLES.PROVIDER,
    }, () => {
      let history = this.props.history;
      let nextRoute = BASE_URL + USERS_STEPS.UPDATE_USER;
      const curURL = new URL(window.location.href);
      console.log("    pushing new route: " + nextRoute);
      history.push({
        pathname: nextRoute,
        search: curURL.search
      });
    });
  }
  
  // Button handler that routes you to /update-credentials so you can change your password
  async goToUpdateCredentials(){
    this.setState({
      'providerUsername': this.state.provider ? this.state.provider.username : '',
      'password': '',
      'newPassword': ''
    });
    let history = this.props.history;
    let nextRoute = BASE_URL + USERS_STEPS.UPDATE_CREDENTIALS;
    const curURL = new URL(window.location.href);
    console.log("    pushing new route: " + nextRoute);
    history.push({
      pathname: nextRoute,
      search: curURL.search
    });
  }

  goToRequestAccess(){
    this.setState({
      'requestClientUsername': '',
      'requestClientClinicianEmail': ''
    })
    let history = this.props.history;
    const curURL = new URL(window.location.href);
    let nextRoute = BASE_URL + USERS_STEPS.REQUEST_ACCESS;
    console.log("    pushing new route: " + nextRoute);
    history.push({
      pathname: nextRoute,
      search: curURL.search
    });
  }

  //Button handler that routes you to /create-client so you can create a new client (currently included on the /practitioner-view page)
  goToCreateClient(){
    //Reset the UI data and any lingering data from a previous client selection, but make sure role is CLIENT
    // then go to the /create-client route
    this.setState({
      'clientUsername': '',
      'clientAlias': '',
      'clientPronouns': ["they", "them", "their"],
      'clientFirstPronoun': "they",
      'selectedClient': null,
    }, () => {
      let history = this.props.history;
      const curURL = new URL(window.location.href);
      let nextRoute = BASE_URL + USERS_STEPS.CREATE_CLIENT;
      console.log("    pushing new route: " + nextRoute);
      history.push({
        pathname: nextRoute,
        search: curURL.search
      });
    });
  }
  
  //Updates / patches a user that is already logged in.
  //ONLY FOR USE with providers (it patches email, alias, and email)
  async updateUser(username, alias, pronouns, email){
    console.log("updateUser, username: " + username);
    var {user, status} = await this.props.updateUser(
      username,
      alias,
      pronouns,
      email,
    );
    console.log("**in updateUser() after updateUser(): " + JSON.stringify(user) + " status: " + status);
    if( status === CREATE_STATUS.PATCH){
      this.setState({
        user: user, 
        userStatus: status}, () => {
          console.log("updateUser state set");
        });
      this.props.updateSession('tempMessage', {
        icon:'success',
        title:'Account Info Updated',
        label: '',
        message: 'Your account info has been updated.',
        duration: 4000
      });
    }
    else{
      alert("Sorry, we were unable to update your info");
    }
  }
  //Updates / patches a user that is already logged in.
  //ONLY FOR USE with clients (it patches alias and pronouns)
  async updateClient(username, alias, pronouns){
    console.log("updateClient, username: " + username);
    var {user, status} = await this.props.updateUser(
      username,
      alias,
      pronouns,
      "" //clients have no email
    );
    console.log("**in updateClient() after updateClient(): " + JSON.stringify(user) + " status: " + status);
    if( status === CREATE_STATUS.PATCH){
      this.setState({
        user: user, 
        userStatus: status}, () => {
          console.log("updateClient state set");
        });
      this.props.updateSession('tempMessage', {
        icon:'success',
        title:'Client Updated',
        label: '',
        message: 'Info for this client has been updated.',
        duration: 4000
      });
    }
    else{
      alert("Sorry, we were unable to update this client's info");
    }
  }

  //Saves the info in local state as a person record in the database, using App.createUser().
  //Used for any user: those who are PROVIDERS and those who are CLIENTS
  //Then sets the user object in the local state (This allows us to check all the user properties later, like this.state.user.role)
  //When creating a new client user, App.createUser() also updates the associated provider (the 'relative') to include the new client's id.
  //An associated client and provider each hold the id of the other in the relationIds property.
  //Can a client be associated with more than one provider? Yes. relationIds allows for that, but our UI doesn't support that explicitly yet. */
  async createNewUser(username, password, alias, pronouns, role, email){
    console.log("createNewUser, username: " + username + " password: " + password + " role: " + role + " provider / relative: " + JSON.stringify(this.state.provider));
    var {user, relative, status} = await this.props.createUser(
      username,
      password,
      alias,
      pronouns,
      role,
      email,
      //if this user is a provider, send in null as the relative. For a client, send in the provider as a relative, associated with that client.
      role === ROLES.PROVIDER ? null : this.state.provider
    );
    console.log("**in createNewUser() after createUser(): " + JSON.stringify(user) + " status: " + status);
    // Relative is the provider associated with the client, in this case of role===2 (CLIENT)
    if(role === ROLES.CLIENT){
      this.setState({
        user: user, 
        userStatus: status,
        provider: relative});
    }
    else{
      this.setState({
        user: user, 
        userStatus: status});
    }
    switch(status){
      case CREATE_STATUS.NOTHING: 
        alert("This username is already in use. Please pick a new username.");
        break;
      default:
        break;
    }
  }

  // Generally understood to be a logout for the provider. (See App.logoutOfProvider(), which simply sets Session.provider to null)
  // Resets the local state to remove traces of the current user.
  // Saves the local state provider to the global Session, which is what effects the 'logout'.
  // TODO we may not want to go to the next section, necessarily.
  logout(){
    this.setState({
      provider: null,
      client: null,
      user: null, 
      userStatus: -1,
      providerUsername: '',
      clientUsername: '',
      password: '',
    }, () => {
      this.saveProviderToSession();
      this.navToNextSection();
    });
  }

  // Uses the parent checkUserCredentials to retrieve a user object from the database if one matches these credentials.
  // Credentials are in local state as username and password.
  // status of 2 means a user was retrieved successfully.
  // Returns true if success, false otw.
  async checkLogin(username, password){
    console.log("** checkLogin: username: " + username + " password: " + password);
    var {user, userJSON, status} = await this.props.checkUserCredentials(username, password); 
    console.log("** user from checkUserCredentials: " + JSON.stringify(user));
    console.log("** status from checkUserCredentials: " + status);

    this.setState({
      user: userJSON, //user ? user.toJSON() : user, 
      userStatus: status
    });
    switch(status){
      case LOGIN_STATUS.NO_USER:
        alert("User doesn't exist");
        break;
      case LOGIN_STATUS.MISMATCH:
        alert("User exists, but password is incorrect");
        break;
      default:
        break;
    }
    return user != null;
  }

  validateEmail(email) {
    console.log("*UsersTab: start validateEmail");

    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    this.setState({
        emailValid: !email ? 0 : re.test(String(email).toLowerCase()) ? 2 : 1
    });
    console.log("*UsersTab: end validateEmail");

  }

  // Called from Reset Password button.
  // **Q** Assumption seems to be that you are logged in, with this.state.providerUsername.
  // But I think there could be a state.username but you are not logged in yet - like you are partway through typing or something
  // actually, App.resetPassword() is designed to handle that, but it is a little
  // confusing when the user field pre-populates with a value but you don't login as that user.
  // Note: Normally user will already have Access Nav loaded in a tab.  Then clicking on emailed link will start a new tab.
  // This creates a 2-tab condition, which AN does not like -- you get One Moment Please... and then after 20ish seconds
  // or so "Application Error -- Access Navigator is already running in another tab. Please switch to using the other tab to avoid data conflicts."
  // Not a friendly password reset situation. But we have to enforce the rule against more than 1 tab. 
  // TODO Maybe tell them to close the tab *when they ask for a reset* Add that to the "Check your email" message ?
  async onResetPassword(){
    if(this.state.providerUsername){
      var success = await this.props.resetPassword(this.state.providerUsername);
      // TODO App.resetPassword() only returns true or false. False could be any sort of failure, not just missing email. 
      if(!success){
        alert("Sorry, there is no email address for this account. So we cannot send you a password reset.");
      }
      else{
        //alert("Please check your email for a password reset link. \n(Before you click the link you receive, close this browser tab.)");
        this.props.updateSession('tempMessage', {
          icon:'success',
          title:'Password Reset Sent',
          label: '',
          message: 'Please check your email for a password reset link. ',
          duration: 10000
        });
      }
    }else
    {
      alert("Please fill in username and choose Forgot Password again.");
    }
  }

  //called from the child form when the user changes anything
  updateLocalState(key) {
    return evt => {
      const target = evt.target; /* which input has been changed */
      let value = key === 'role' ? Number(target.value) : 
        target.nodeName === 'LI' ? target.attributes.header.value : 
        target.type === 'checkbox' ? target.checked :
        target.value;
      if(key === 'selectedClient'){
        if(value !== this.state.selectedClient){
          this.setState({'selectedClient': value});
        }else{
          this.setState({'selectedClient': null}); //if you click the same client, it should unselect it
        }
      }else{
        this.setState({ [key]: value });
      }
      if(key === 'email'){
        this.validateEmail(value);
      }
      if(key === 'providerFirstPronoun'){
        let pronouns;
        switch(value){
          case 'he':
            pronouns = ['he', 'him', 'his'];
            break;
          case 'she':
            pronouns = ['she', 'her', 'her'];
            break;
          default:
            pronouns = ["they", "them", "their"];
            break;
        }
        this.setState({'providerPronouns': pronouns});
      }
      if(key === 'clientFirstPronoun'){
        let pronouns;
        switch(value){
          case 'he':
            pronouns = ['he', 'him', 'his'];
            break;
          case 'she':
            pronouns = ['she', 'her', 'her'];
            break;
          default:
            pronouns = ["they", "them", "their"];
            break;
        }
        this.setState({'clientPronouns': pronouns});
      }
    }
  }


  render() {
    const curURL = new URL(window.location.href);
    const searchQuery = curURL.search;
    return(
    <>
    <Switch>
      <Route path={BASE_URL + steps.CREATE}>
        <UsersCreate
            key={steps.CREATE}
            loggedIn={this.state.provider ? true : false}
            username={this.state.providerUsername}
            onChangeUsername={this.updateLocalState("providerUsername")}
            password={this.state.password}
            onChangePassword={this.updateLocalState("password")}
            alias={this.state.providerAlias}
            onChangeAlias={this.updateLocalState("providerAlias")}
            email={this.state.email}
            emailValid={this.state.emailValid}
            onChangeEmail={this.updateLocalState("email")}
            userStatus={this.state.userStatus}
            onContinue={this.onContinue}
          />
      </Route>
      <Route path={BASE_URL + steps.LOGIN}>
        <UsersLogin
            key={steps.LOGIN}
            username={this.state.providerUsername}
            onChangeUsername={this.updateLocalState("providerUsername")}
            password={this.state.password}
            onChangePassword={this.updateLocalState("password")}
            userStatus={this.state.userStatus}
            stayLoggedIn={this.state.stayLoggedIn}
            onChangeStayLoggedIn={this.updateLocalState("stayLoggedIn")}
            onResetPassword={this.onResetPassword}
            onContinue={this.onContinue}
          />
      </Route>
      <Route path={BASE_URL + steps.UPDATE_USER}>
        <UsersUpdate
            key={steps.UPDATE_USER}
            loggedIn={this.state.provider ? true : false}
            username={this.state.providerUsername}
            alias={this.state.providerAlias}
            onChangeAlias={this.updateLocalState("providerAlias")}
            pronoun={this.state.providerFirstPronoun}
            onChangePronoun={this.updateLocalState("providerFirstPronoun")}
            email={this.state.email}
            emailValid={this.state.emailValid}
            onChangeEmail={this.updateLocalState("email")}
            goToUpdateCredentials={this.goToUpdateCredentials}
            onContinue={this.onContinue}
          />
      </Route>
      <Route path={BASE_URL + steps.UPDATE_CREDENTIALS}>
        <UsersUpdateCredentials
            key={steps.UPDATE_CREDENTIALS}
            username={this.state.providerUsername}
            onChangeUsername={this.updateLocalState("providerUsername")}
            oldPassword={this.state.password}
            onChangeOldPassword={this.updateLocalState("password")}
            newPassword={this.state.newPassword}
            onChangeNewPassword={this.updateLocalState("newPassword")}
            userStatus={this.state.userStatus}
            onContinue={this.onContinue}
          />
      </Route>
      <Route path={BASE_URL + steps.UPDATE_CLIENT}>
        <UsersUpdateClient
            key={steps.UPDATE_USER_CLIENT}
            loggedIn={this.state.provider ? true : false}
            username={this.state.clientUsername}
            alias={this.state.clientAlias}
            onChangeAlias={this.updateLocalState("clientAlias")}
            pronoun={this.state.clientFirstPronoun}
            onChangePronoun={this.updateLocalState("clientFirstPronoun")}
            onContinue={this.onContinue}
          />
      </Route>
      <Route path={BASE_URL + steps.RESET_PASSWORD}>
        <UsersResetPassword
            key={steps.RESET_PASSWORD}
            resetToken={this.state.resetToken}
            newPassword={this.state.newPassword}
            onChangeNewPassword={this.updateLocalState("newPassword")}
            userStatus={this.state.userStatus}
            onContinue={this.onContinue}
          />
      </Route>
      <Route path={BASE_URL + steps.PRACTITIONER_VIEW}>
        <UsersProviderView
            key={steps.PRACTITIONER_VIEW}
            provider={this.state.provider}
            clients={this.state.providerClients}
            selectedClient={this.state.selectedClient}
            onClientClicked={this.updateLocalState("selectedClient")}
            goToCreateClient={this.goToCreateClient}
            goToRequestAccess={this.goToRequestAccess}
            goToUpdateUser={this.goToUpdateUser}
            goToUpdateCredentials={this.goToUpdateCredentials}
            onContinue={this.onContinue}
          />
      </Route>
      <Route path={BASE_URL + steps.CREATE_CLIENT}>
        <UsersCreateClient
            key={steps.CREATE_CLIENT}
            loggedIn={this.state.provider ? true : false}
            username={this.state.clientUsername}
            onChangeUsername={this.updateLocalState("clientUsername")}
            alias={this.state.clientAlias}
            onChangeAlias={this.updateLocalState("clientAlias")}
            pronoun={this.state.clientFirstPronoun}
            onChangePronoun={this.updateLocalState("clientFirstPronoun")}
            userStatus={this.state.userStatus}
            onContinue={this.onContinue}
          />
      </Route>
      <Route path={BASE_URL + steps.REVIEW}>
        <UsersReview
            key={steps.REVIEW}
            provider={this.state.provider}
            client={this.state.client}
            onContinue={this.onContinue}
          />
      </Route>
      <Route path={BASE_URL + steps.REQUEST_ACCESS}>
        <UsersRequestAccess
          key={steps.REQUEST_ACCESS}
          loggedIn={this.state.provider ? true : false}
          currentClinicianEmail={this.state.requestClientClinicianEmail}
          onChangeCurrentClinicianEmail={this.updateLocalState("requestClientClinicianEmail")}
          usernameToRequest={this.state.requestClientUsername}
          onChangeUsernameToRequest={this.updateLocalState("requestClientUsername")}
          onContinue={this.onContinue}
      />
      </Route>
      <Route path={BASE_URL}>
        <Redirect to={BASE_URL + steps.LOGIN + searchQuery} />
      </Route>
    </Switch>
    </>
    );
  }
}

UsersTab.propTypes = propTypes;
UsersTab.defaultProps = defaultProps;


// withRouter wrapper provides access to the match, location, and history props
// "You can get access to the history object’s properties and the closest <Route>'s match 
// via the withRouter higher-order component. withRouter will pass updated 
// match, location, and history props to the wrapped component whenever it renders"
export default withRouter(UsersTab);
